// services/PartnerService.ts

import axios from 'axios';
import { baseUrl } from '../utils/config';
import { AuthService } from './AuthService';
import { id } from 'date-fns/locale';

// Category mapping with synonyms and lightweight fuzzy matching
const CATEGORY_CATALOG: Array<{ id: string; name: string; keywords: string[] }> = [
    { id: '1', name: 'Sport Wellness', keywords: ['sport', 'sports', 'fitness', 'wellness', 'gym', 'workout', 'training'] },
    { id: '2', name: 'Beauty & Cosmetics', keywords: ['beauty', 'cosmetic', 'cosmetics', 'makeup', 'hair', 'skincare', 'care'] },
    { id: '3', name: 'OTC Medicine', keywords: ['otc', 'medicine', 'med', 'drug', 'pharmacy', 'over the counter', 'remedy'] },
    { id: '4', name: 'Special Offers', keywords: ['special', 'offers', 'offer', 'deal', 'deals', 'sale', 'discount', 'promo'] },
    { id: '5', name: 'Vitamins', keywords: ['vitamin', 'vitamins', 'supplement', 'supplements', 'minerals', 'nutrient'] },
    { id: '6', name: 'Skin Care', keywords: ['skin', 'skincare', 'derma', 'dermatology', 'cream', 'lotion', 'serum'] },
    { id: '7', name: 'Women care', keywords: ['women', 'woman', 'female', 'feminine', 'lady', 'maternity', 'pregnancy'] },
    { id: '8', name: 'Hygiene care', keywords: ['hygiene', 'personal care', 'sanitary', 'clean', 'soap', 'shampoo', 'wash'] },
    { id: '9', name: 'Baby care', keywords: ['baby', 'infant', 'newborn', 'kid', 'kids', 'child', 'children', 'diaper'] },
];

function normalizeText(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function tokenSet(input: string): Set<string> {
    return new Set(normalizeText(input).split(' ').filter(Boolean));
}

function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
    const intersection = new Set([...a].filter(x => b.has(x)));
    const union = new Set([...a, ...b]);
    return union.size === 0 ? 0 : intersection.size / union.size;
}

export function mapCategoryToId(categoryInput: string | undefined | null): string {
    const input = (categoryInput || '').toString();
    const normalized = normalizeText(input);
    if (!normalized) throw new Error('Invalid category');

    // If user already provided a numeric id between 1..9, accept it
    if (/^[1-9]$/.test(normalized)) return normalized;

    // Exact name match (normalized)
    for (const c of CATEGORY_CATALOG) {
        if (normalizeText(c.name) === normalized) return c.id;
    }

    // Keyword containment match
    for (const c of CATEGORY_CATALOG) {
        const nameNorm = normalizeText(c.name);
        if (normalized.includes(nameNorm) || nameNorm.includes(normalized)) return c.id;
        for (const kw of c.keywords) {
            const nkw = normalizeText(kw);
            if (nkw && (normalized.includes(nkw) || nkw.includes(normalized))) return c.id;
        }
    }

    // Token-based similarity fallback – pick the best jaccard score
    const inputTokens = tokenSet(normalized);
    let best: { id: string; score: number } | null = null;
    for (const c of CATEGORY_CATALOG) {
        const nameTokens = tokenSet(c.name);
        const keywordTokens = new Set<string>([...c.keywords.flatMap(k => Array.from(tokenSet(k)))]);
        const allTokens = new Set<string>([...nameTokens, ...keywordTokens]);
        const score = jaccardSimilarity(inputTokens, allTokens);
        if (!best || score > best.score) best = { id: c.id, score };
    }
    // Require a minimal similarity to avoid wrong mappings
    if (best && best.score >= 0.25) return best.id;

    throw new Error(`Invalid category: ${categoryInput}`);
}

export type CreatePartnerPayload = {
    nom: string;
    prenom: string;
    email: string;
    mot_de_passe: string;
    nom_entreprise: string; // type ( medecin or coach)
    identifiant_fiscal: string;
    activite: string;// specialite
    adresse_pays: string;
    adresse_ville: string;
    adresse_code_postal: string;
    fichier_convention: string;
    email_contact: string;
    photo_url?: string; // Optional profile picture filename or URL
};

export type CreateProfessionalPayload = {
    utilisateur_id: number;
    type_profil: 'Coach' | 'Médecin'; // Assuming only two types, extend if needed
    specialites: string[];
    sous_specialites: string;
    numero_ordre_medecins: string;
    date_naissance: string; // ISO format 'YYYY-MM-DD'
    pays: string;
    telephone: string;
    honoraire_montant: number;
    honoraire_devise: string;
    rib: string;
    banque: string;
    agence: string;
    code_swift: string;
    langues: string[];
    pays_exercice: string[];
    info_supplementaires: string;
};

export interface Partner {
    utilisateur_id: number;
    nom_entreprise: string;
    identifiant_fiscal: string;
    activite: string;
    adresse_pays: string;
    adresse_ville: string;
    adresse_code_postal: string;
    fichier_convention: string;
    email_contact: string;
    id: number;
    photo_url?: string; // Optional profile picture filename or URL
    informations_supplementaires?: string; // Optional additional information
}
interface Utilisateur {
    id: number;
    nom: string;
    prenom: string;
    email: string;
    adresse: string | null;
    ville: string | null;
    pays: string | null;
    code_postal: string | null;
    telephone: string | null;
    mot_de_passe: string;
    role: string;
    nationalite: string | null;
    date_naissance: string | null; // ISO format 'YYYY-MM-DD'
}

export interface PartnerDetails{
    // Add any additional fields specific to partner details here
    partenaire: Partner;
    utilisateur: Utilisateur;
}


export const PartnerService = {
    api: (() => {
        const instance = axios.create({ baseURL: baseUrl });
        instance.interceptors.request.use((config) => {
            const token = localStorage.getItem('auth_token');
            if (token) {
                config.headers = config.headers || {};
                (config.headers as any).Authorization = `Bearer ${token}`;
            }
            return config;
        });
        return instance;
    })(),

    async getPartnerDetailsByMail(email: string): Promise<any> {
        try {
            const response = await this.api.get<any>(`${baseUrl}/partenaires/details/by-email/${email}`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération du partenaire:', error);
            throw error;
        }
    },

    async createPartner(data: CreatePartnerPayload): Promise<void> {
        // 1. Extract user-related fields for user creation
        const userPayload = {
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            mot_de_passe: data.mot_de_passe,
            role: 'partenaire',
        };

        let utilisateur_id: number | undefined;
        try {
            // Create the user first
            const userRes = await this.api.post(`${baseUrl}/api/utilisateurs/`, userPayload);
            utilisateur_id = userRes.data.id;

            if (!utilisateur_id) {
                throw new Error('Utilisateur ID not returned from user creation response.');
            }

            // 2. Compose partner creation payload
            const partnerPayload = {
                utilisateur_id,
                nom_entreprise: data.nom_entreprise,
                identifiant_fiscal: data.identifiant_fiscal,
                activite: data.activite,
                adresse_pays: data.adresse_pays,
                adresse_ville: data.adresse_ville,
                adresse_code_postal: data.adresse_code_postal,
                fichier_convention: data.fichier_convention,
                email_contact: data.email_contact,
            };

            // 3. Send second request to create the partner
            await this.api.post(`${baseUrl}/partenaires/`, partnerPayload);
        } catch (error) {
            // If partner creation fails, delete the user that was just created to avoid orphaned users
            if (utilisateur_id) {
                try {
                    await AuthService.deleteUser(utilisateur_id);
                } catch (deleteError) {
                    // Log if user deletion also fails
                    console.error('Failed to delete user after partner creation failed:', deleteError);
                }
            }
            if (axios.isAxiosError(error)) {
                const detail = error.response?.data?.detail;
                throw detail;
            }
            throw error;
        }
    },

    async deletePartnerUser(partnerId: number, userId: number): Promise<void> {
        try {
            await this.deletePartner(partnerId);
            await AuthService.deleteUser(userId);

        } catch (error: unknown) {
            console.error('Erreur lors de la suppression du partenaire:', error);
            throw error;
        }
    },




    async updatePartner(data: any): Promise<any> {
        const userId = data.user_id;
        const partnerId = data.partner_id;
        console.log('Updating partner with data:', data);
        const userPayload = {
            id  : userId,
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            mot_de_passe: data.mot_de_passe,
            nationalite: data.nationalite,
            date_naissance: data.date_naissance,
            telephone: data.telephone,
            ville: data.ville,
        };
        await AuthService.updateUser(userId, userPayload);
        try {
            const partnerPayload = {
                utilisateur_id: userId,
                nom_entreprise: data.nom_entreprise,
                identifiant_fiscal: data.identifiant_fiscal,
                activite: data.activite,
                adresse_pays: data.adresse_pays,
                adresse_ville: data.adresse_ville,
                adresse_code_postal: data.adresse_code_postal,
                fichier_convention: data.fichier_convention,
                email_contact: data.email_contact,
                photo_url: data.photo_url || null, // Optional profile picture
                informations_supplementaires: data.informations_supplementaires || '',
            };

            // 3. Send request to update the partner
            await this.api.put(`${baseUrl}/partenaires/${partnerId}`, partnerPayload);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const detail = error.response?.data?.detail;
                throw detail;
            }
            throw error;
        }
    },

    async deletePartner(partnerId: number): Promise<void> {
        try {
            await this.api.delete(`${baseUrl}/partenaires/${partnerId}`);
        } catch (error: unknown) {
            console.error('Erreur lors de la suppression du partenaire:', error);
            throw error;
        }
    },


    async createProfetional(data: CreatePartnerPayload): Promise<void> {
        // 1. Extract user-related fields for user creation
        const userPayload = {
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            mot_de_passe: data.mot_de_passe,
            
        };

        let utilisateur_id: number | undefined;
        try {
            // Create the user first
            const userRes = await axios.post(`${baseUrl}/api/utilisateurs/`, userPayload);
            utilisateur_id = userRes.data.id;

            if (!utilisateur_id) {
                throw new Error('Utilisateur ID not returned from user creation response.');
            }

            // 2. Compose partner creation payload
            const partnerPayload = {
                utilisateur_id,
                nom_entreprise: data.nom_entreprise,
                identifiant_fiscal: data.identifiant_fiscal,
                activite: data.activite,
                adresse_pays: data.adresse_pays,
                adresse_ville: data.adresse_ville,
                adresse_code_postal: data.adresse_code_postal,
                fichier_convention: data.fichier_convention,
                email_contact: data.email_contact,
            };

            // 3. Send second request to create the partner
            await axios.post(`${baseUrl}/partenaires/`, partnerPayload);
        } catch (error) {
            // If partner creation fails, delete the user that was just created to avoid orphaned users
            if (utilisateur_id) {
                try {
                    await AuthService.deleteUser(utilisateur_id);
                } catch (deleteError) {
                    // Log if user deletion also fails
                    console.error('Failed to delete user after partner creation failed:', deleteError);
                }
            }
            if (axios.isAxiosError(error)) {
                const detail = error.response?.data?.detail;
                throw detail;
            }
            throw error;
        }
    },

    async getAllPartners(): Promise<Partner[]> {
        try {
            const response = await this.api.get<Partner[]>(`${baseUrl}/partenaires/`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors du chargement des partenaires:', error);
            throw error;
        }
    },
    async getAllPartnersDetails(): Promise<PartnerDetails[]> {
        try {
            const response = await this.api.get<PartnerDetails[]>(`${baseUrl}/partenaires/details`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors du chargement des partenaires:', error);
            throw error;
        }
    },

    // Utility to extract partner_id from userInfo 
    getPartnerIdFromUserInfo(userInfo: any): number | undefined {
        if (!userInfo) return undefined;
        // If userInfo has a nested partenaire object
        if (userInfo.partenaire && userInfo.partenaire.id) {
            return userInfo.partenaire.id;
        }
        // If userInfo is flat (legacy)
        if (userInfo.id) {
            return userInfo.id;
        }
        return undefined;
    },

    async createProductByPartnerId(product: {
        name: string;
        price: number | string;
        description: string;
        category: string;
        inStock: boolean;
        stock_number: number;
        composant_chimique: string;
        instructions?: string;
        brand_names?: string[];
    }): Promise<void> {
        try {
            // Get partner_id from localStorage
            const userInfoRaw = localStorage.getItem("userInfo");
            let userInfo;
            try {
                userInfo = JSON.parse(userInfoRaw || '""');
            } catch (e) {
                console.error('Error parsing userInfo from localStorage:', userInfoRaw, e);
                throw new Error('Invalid userInfo in localStorage');
            }
            console.log('userInfo from localStorage:', userInfo);
            const partner_id = this.getPartnerIdFromUserInfo(userInfo);
            if (!partner_id) {
                console.error('No partner_id found in userInfo:', userInfo);
                throw new Error('No partner_id found');
            }
            // Map category string to id (with fuzzy mapping)
            const category_id = mapCategoryToId(product.category);
            // Generate product id
            const now = new Date();
            const id = `prod_${now.getTime()}`;
            // Prepare payload
            const payload = {
                name: product.name,
                price: product.price,
                id,
                description: product.description,
                instructions: product.instructions || null,
                category_id,
                imageData: null,
                imageMimeType: null,
                inStock: product.inStock,
                partner_id,
                stock_number: product.stock_number,
                composant_chimique: product.composant_chimique,
                brand_names: product.brand_names || null
            };
            console.log('Incoming product argument:', product);
            console.log('Creating product with payload:', payload);
            await this.api.post(`${baseUrl}/products`, payload);
            console.log('Product created successfully');
        } catch (error) {
            console.error('Error creating product:', error);
            throw error;
        }
    },

    /**
     * Fetch products by partner with pagination.
     * @param {number|string} partner_id - The partner's ID
     * @param {number} skip - Number of products to skip (for pagination)
     * @param {number} limit - Number of products to fetch (for pagination)
     * @returns {Promise<{products: any[], total: number}>} - List of products with total count
     */
    async getProductByPartner(partner_id: number | string, skip: number = 0, limit: number = 20): Promise<{products: any[], total: number}> {
        try {
            // Updated endpoint with pagination
            const response = await this.api.get(`${baseUrl}/products/partner/${partner_id}/`, {
                params: { skip, limit }
            });
                // Process the response to ensure instructions field is present
                const processProducts = (products: any[]) => {
                    return products.map(product => ({
                        ...product,
                        instructions: product.instructions || ''  // Ensure instructions field exists
                    }));
                };

                // Backend now returns { products: [...], total: number, skip: number, limit: number }
                if (response.data && Array.isArray(response.data.products)) {
                    return {
                        products: processProducts(response.data.products),
                        total: response.data.total || response.data.products.length
                    };
                }
                // Fallback for old format - direct array
                const products = processProducts(Array.isArray(response.data) ? response.data : []);
                return {
                    products,
                    total: products.length
                };
        } catch (error) {
            console.error('Erreur lors de la récupération des produits du partenaire:', error);
            throw error;
        }
    },

    /**
     * PRODUCT IMAGE LOGIC (NEW, supports multiple images per product)
     */

    /**
     * Upload a new product image (base64). Supports multiple images per product.
     * @param {string|number} productId - The product ID
     * @param {string} imageBase64 - The image as a base64 string (with data:image/... prefix)
     * @param {boolean} isMain - Whether this image is the main image for the product
     * @returns {Promise<any>} - The response data (image object)
     */
    async uploadProductImage(productId: string | number, imageBase64: string, isMain: boolean): Promise<any> {
        try {
            const payload = {
                product_id: productId,
                image_base64: imageBase64,
                is_main: isMain,
            };
            // Updated endpoint
            const response = await this.api.post(`${baseUrl}/product-images/upload`, payload);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de l\'envoi de l\'image du produit:', error);
            throw error;
        }
    },

    /**
     * Fetch all product images for a given product.
     * @param {string|number} productId - The product ID
     * @returns {Promise<any[]>} - List of image objects
     */
    async getProductImages(productId: string | number): Promise<any[]> {
        try {
            // Updated endpoint
            const response = await this.api.get(`${baseUrl}/product-images/${productId}`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des images du produit:', error);
            throw error;
        }
    },

    /**
     * Delete a specific product image by its image id.
     * @param {string|number} imageId - The image ID
     * @returns {Promise<{ message: string }>} - The response message
     */
    async deleteProductImage(imageId: string | number): Promise<{ message: string }> {
        try {
            // Updated endpoint
            const response = await this.api.delete(`${baseUrl}/product-images/${imageId}`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'image du produit:', error);
            throw error;
        }
    },

    async deleteProductById(product_id: string | number): Promise<void> {
        try {
            // NEW: Delete all product images first, but don't block product deletion if photo deletion fails
            let images = [];
            try {
                images = await this.getProductImages(product_id);
            } catch (imgErr) {
                console.error('Error fetching product images (ignored):', imgErr);
            }
            for (const img of images) {
                try {
                    await this.deleteProductImage(img.id);
                } catch (imgErr) {
                    console.error('Failed to delete product image (ignored):', img.id, imgErr);
                }
            }
            // Then delete the product itself
            await this.api.delete(`${baseUrl}/products/${product_id}`);
        } catch (error) {
            console.error('Erreur lors de la suppression du produit:', error);
            throw error;
        }
    },

    async updateProductByPartnerId(product: {
        id: number | string;
        name: string;
        price: number | string;
        description: string;
        category: string;
        inStock: boolean;
        stock_number: number;
        composant_chimique: string;
        imageData?: string | null;
        imageMimeType?: string | null;
        brand_names?: string[];
        instructions?: string;
    }): Promise<void> {
        try {
            // Get partner_id from localStorage
            const userInfoRaw = localStorage.getItem("userInfo");
            let userInfo;
            try {
                userInfo = JSON.parse(userInfoRaw || '""');
            } catch (e) {
                console.error('Error parsing userInfo from localStorage:', userInfoRaw, e);
                throw new Error('Invalid userInfo in localStorage');
            }
            const partner_id = this.getPartnerIdFromUserInfo(userInfo);
            if (!partner_id) {
                console.error('No partner_id found in userInfo:', userInfo);
                throw new Error('No partner_id found');
            }
            // Map category string to id (with fuzzy mapping)
            const category_id = mapCategoryToId(product.category);
            // Prepare payload for update
            const payload = {
                id: product.id,
                name: product.name,
                price: product.price,
                description: product.description,
                category_id,
                imageData: product.imageData || null,
                imageMimeType: product.imageMimeType || null,
                inStock: product.inStock,
                partner_id,
                stock_number: product.stock_number,
                composant_chimique: product.composant_chimique,
                brand_names: product.brand_names || null,
                instructions: product.instructions || null
            };
            console.log('Updating product with payload:', payload);
            // Try to update product, even if photo service fails
            let productUpdateError = null;
            try {
                await this.api.post(`${baseUrl}/products`, payload);
            } catch (err) {
                console.error('Error updating product (main request):', err);
                productUpdateError = err;
            }
            // Try to update photo, but ignore errors
            if (product.imageData || product.imageMimeType) {
                try {
                    // Example: await this.uploadProductImage(product.id, product.imageData, true);
                    // (Assume uploadProductImage is used elsewhere in the UI, so just log here)
                } catch (photoErr) {
                    console.error('Photo update failed (ignored):', photoErr);
                }
            }
            if (productUpdateError) throw productUpdateError;
        } catch (error) {
            console.error('Error updating product:', error);
            throw error;
        }
    },

    async getProducts(): Promise<any[]> {
        try {
            const response = await this.api.get(`${baseUrl}/products`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des produits:', error);
            throw error;
        }
    },

    async getOrdersByPartner(partner_id: number | string): Promise<any[]> {
        try {
            const response = await this.api.get(`${baseUrl}/orders/by-partner/${partner_id}`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération des commandes du partenaire:', error);
            throw error;
        }
    },

    async getPartnerStats(partner_id: number | string): Promise<any> {
        try {
            const response = await this.api.get(`${baseUrl}/partner/${partner_id}/stats`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des statistiques du partenaire:', error);
            throw error;
        }
    },

    async getPartnerRevenue(partner_id: number | string, interval: 'week' | 'month' | 'year'): Promise<any> {
        try {
            const response = await this.api.get(`${baseUrl}/partner/${partner_id}/revenue`, { params: { interval } });
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du revenu du partenaire:', error);
            throw error;
        }
    },

    /**
     * Upload a partner's photo
     * @param {number|string} partnerId - The partner's ID
     * @param {File} file - The image file to upload
     * @returns {Promise<any>} - The response data
     */
    async uploadPartnerPhoto(partnerId: number | string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await this.api.post(`${baseUrl}/upload-photo/partenaire/${partnerId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error: any) {
            console.error("Erreur lors de l'envoi de la photo du partenaire:", error);
            throw error;
        }
    },

   
};
